import {
  ButtonVariants,
  CardViewEnum,
  DropdownItem,
  EMPTY_OBJECT,
  FilterInput,
  FormControlSizes,
  IconName,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventProperty,
  Portal,
  TabIconButton,
  Tooltip,
  useDisclosure,
  useMixpanel,
  type PredictionMarketCardConfig,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v1 as uuid } from 'uuid';
import { useFeatureFlag as useAppFeatureFlag } from '../../hooks/useFeatureFlag';
import { fillToNumColumns } from '../../providers/AppConfigProvider/fillToNumColumns';
import type { PredictionMarketPanelConfig } from '../../types/LayoutConfig';
import { TabSettingsDialog } from '../AppLayout/Panels/Trading/MarketDataCardsPanel/TabSettingsDialog';
import { Panel } from '../Layout/Components/Panel';
import { PredictionMarketCards, type PredictionMarketCardsProps } from './PredictionMarketCards';

export function PredictionMarketsPanel({ api, params }: IDockviewPanelProps<PredictionMarketPanelConfig>) {
  const id = api.id;
  const tabMenuPortalId = `tabMenu_${id}`;
  const rightComponentPortalId = `rightComponent_${id}`;
  const { enablePredictionMarkets } = useAppFeatureFlag();
  const { columns: persistedColumns = [], sortCards = false } = params;
  const [cardFilter, setCardFilter] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isShowingPlaceholders, setIsShowingPlaceholders] = useState(false);
  const mixpanel = useMixpanel();
  const tabSettingsDialog = useDisclosure();

  const columns = useMemo(() => {
    const nextColumns = fillToNumColumns(persistedColumns);
    return isAdding ? nextColumns : nextColumns.map(excludeNewCardState);
  }, [isAdding, persistedColumns]);

  const columnsRef = useRef(columns);
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const updateColumns = useCallback(
    (
      nextColumns:
        | PredictionMarketCardConfig[][]
        | ((previousColumns: PredictionMarketCardConfig[][]) => PredictionMarketCardConfig[][])
    ) => {
      const resolvedColumns = typeof nextColumns === 'function' ? nextColumns(columnsRef.current) : nextColumns;
      columnsRef.current = resolvedColumns;
      api.updateParameters({ columns: resolvedColumns });
    },
    [api]
  );

  const handleSortCards = useCallback(() => {
    mixpanel.track(MixpanelEvent.SortMarketsTab);
    api.updateParameters({ sortCards: !sortCards });
  }, [api, mixpanel, sortCards]);

  const handleToggleAddingCard = useCallback(() => {
    mixpanel.track(!isAdding ? MixpanelEvent.AddCardStart : MixpanelEvent.AddCardCancel);
    setIsShowingPlaceholders(!isAdding);
    setIsAdding(!isAdding);
    api.updateParameters({ columns: columns.map(excludeNewCardState) });
  }, [api, columns, isAdding, mixpanel]);

  const createCard = useCallback(
    (index: number) => {
      mixpanel.track(MixpanelEvent.CreateMarketCard);
      updateColumns(previousColumns =>
        previousColumns.map((column, columnIndex) =>
          columnIndex === index
            ? [
                {
                  id: uuid(),
                  state: MarketDataCardState.NEW_CARD,
                  eventID: '',
                  eventSymbol: '',
                  eventTitle: '',
                  view: CardViewEnum.NORMAL,
                },
                ...column,
              ]
            : column
        )
      );
      setIsShowingPlaceholders(false);
    },
    [mixpanel, updateColumns]
  );

  const moveCard = useCallback<PredictionMarketCardsProps['moveCard']>(
    result => {
      const { source, destination, draggableId } = result;
      if (!destination) {
        return;
      }

      const sourceColumnIndex = parseInt(source.droppableId);
      const destinationColumnIndex = parseInt(destination.droppableId);
      const card = columnsRef.current.flat().find(columnCard => columnCard.id === draggableId);

      if (card === undefined || (sourceColumnIndex === destinationColumnIndex && source.index === destination.index)) {
        return;
      }

      mixpanel.track(MixpanelEvent.MovedCard, { [MixpanelEventProperty.ColumnIndex]: destination.index });
      updateColumns(previousColumns =>
        previousColumns.map((column, columnIndex) => {
          let nextColumn = [...column];
          if (columnIndex === sourceColumnIndex) {
            nextColumn = nextColumn.filter(columnCard => columnCard.id !== draggableId);
          }
          if (columnIndex === destinationColumnIndex) {
            nextColumn.splice(destination.index, 0, card);
          }
          return nextColumn;
        })
      );
    },
    [mixpanel, updateColumns]
  );

  const saveCard = useCallback(
    (nextCard: PredictionMarketCardConfig) => {
      mixpanel.track(MixpanelEvent.UpdateMarketCard);
      updateColumns(previousColumns =>
        previousColumns.map(column =>
          column.map(previousCard => {
            if (previousCard.id !== nextCard.id) {
              return previousCard;
            }

            const mergedCard: PredictionMarketCardConfig = {
              ...previousCard,
              ...nextCard,
              state: MarketDataCardState.UPDATED_CARD,
            };
            if (!('outcomeSymbol' in nextCard)) {
              delete mergedCard.outcomeSymbol;
            }
            return mergedCard;
          })
        )
      );
      setIsAdding(false);
    },
    [mixpanel, updateColumns]
  );

  const deleteCard = useCallback(
    (card: PredictionMarketCardConfig) => {
      mixpanel.track(MixpanelEvent.DeleteCard);
      updateColumns(previousColumns =>
        previousColumns.map(column => column.filter(previousCard => previousCard.id !== card.id))
      );
    },
    [mixpanel, updateColumns]
  );

  const handleConfirm = useCallback(
    ({ label }: { defaults: Record<string, any>; label: string }) => {
      mixpanel.track(MixpanelEvent.SaveMarketTabSettings);
      tabSettingsDialog.close();
      api.setTitle(label);
      if (label !== api.title) {
        mixpanel.track(MixpanelEvent.RenameTab);
        api.updateParameters({ customTitle: true });
      }
    },
    [api, mixpanel, tabSettingsDialog]
  );

  const hidePlaceholders = useCallback(() => {
    setIsShowingPlaceholders(false);
  }, []);

  if (!enablePredictionMarkets) {
    return null;
  }

  return (
    <>
      <TabSettingsDialog
        {...tabSettingsDialog}
        label={api.title ?? ''}
        defaults={EMPTY_OBJECT}
        showSubAccount={false}
        canDeleteTab
        onConfirm={handleConfirm}
        onCancel={() => {
          mixpanel.track(MixpanelEvent.CloseMarketTabSettings);
          tabSettingsDialog.close();
        }}
        onDelete={() => {
          tabSettingsDialog.close();
          api.close();
        }}
        showCancel={false}
        showConfirm={false}
        showClose
        width={400}
      />
      <Portal portalId={rightComponentPortalId}>
        <Tooltip usePortal={false} tooltip="Settings for current prediction market tab" placement="bottom">
          <TabIconButton
            onClick={tabSettingsDialog.open}
            icon={IconName.Cog}
            ghost={!tabSettingsDialog.isOpen}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Filter prediction cards by event or outcome" placement="bottom">
          <FilterInput
            value={cardFilter}
            onChange={setCardFilter}
            placeholder="By event or outcome"
            mixpanelEvent={MixpanelEvent.SearchMarketsTab}
            data-testid="filter-prediction-cards-button"
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Sort cards by event and outcome" placement="bottom">
          <TabIconButton
            onClick={handleSortCards}
            icon={IconName.SortDescending}
            ghost={!sortCards}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
          />
        </Tooltip>
        <Tooltip usePortal={false} tooltip="Add prediction card" placement="bottom">
          <TabIconButton
            onClick={handleToggleAddingCard}
            icon={IconName.Calendar}
            ghost={!isAdding}
            variant={ButtonVariants.Priority}
            size={FormControlSizes.Small}
            data-testid="add-prediction-card-button"
          />
        </Tooltip>
      </Portal>
      <Portal portalId={tabMenuPortalId}>
        <DropdownItem
          startIcon={IconName.Cog}
          onClick={() => {
            tabSettingsDialog.open();
            mixpanel.track(MixpanelEvent.ViewMarketsTabSettings);
          }}
        >
          Settings
        </DropdownItem>
      </Portal>
      <Panel panelApi={api} data-testid="prediction-markets-panel">
        <PredictionMarketCards
          columns={columns}
          cardFilter={cardFilter}
          sortCards={sortCards}
          columnsTabID={id}
          selectedTabID={id}
          isAddingCard={isAdding}
          isShowingPlaceholders={isShowingPlaceholders}
          moveCard={moveCard}
          createCard={createCard}
          deleteCard={deleteCard}
          saveCard={saveCard}
          hidePlaceholders={hidePlaceholders}
          onToggleAddingCard={handleToggleAddingCard}
        />
      </Panel>
    </>
  );
}

const excludeNewCardState = (column: PredictionMarketCardConfig[]): PredictionMarketCardConfig[] =>
  column.filter(card => card.state !== MarketDataCardState.NEW_CARD);
